import {
  CalendarDaysIcon,
  MapPinIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  Tooltip,
} from "@heroui/react";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "../../utils/toast";
import {
  deleteEvent,
  fetchCountries,
  getAllEvents,
  getEventDetail,
} from "../../services/api";
import {
  EventDetailResponse,
  EventListResponse,
} from "../../types/types";
import CreateEditEvent from "./createEditEvent";
import EventFilters from "./eventFilters";

const getSafeLocale = (locale?: string): string | undefined => {
  const cleanedLocale = locale?.replace(/['"]/g, "").trim();

  if (!cleanedLocale) return undefined;

  try {
    new Intl.DateTimeFormat(cleanedLocale);
    return cleanedLocale;
  } catch {
    return undefined;
  }
};

const formatDisplayDate = (
  dateString: string | null,
  fallbackLabel: string,
  locale?: string
): string => {
  if (!dateString) return fallbackLabel;

  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split("-").map(Number);
      const date = new Date(year, month - 1, day);

      return date.toLocaleDateString(getSafeLocale(locale), {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }

    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      return dateString;
    }

    return date.toLocaleDateString(getSafeLocale(locale), {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
};

const formatEventLocation = (event: EventListResponse): string =>
  event.country || event.location || event.city || "International";

const getEventStartDate = (event: EventListResponse): string | null =>
  event.startDate || null;

const Events: React.FC = () => {
  const [eventsList, setEventsList] = useState<EventListResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [locationFilter, setLocationFilter] = useState<string>("");
  const [apiLocations, setApiLocations] = useState<string[]>([]);

  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);
  const [editingEventData, setEditingEventData] =
    useState<EventDetailResponse | null>(null);

  const [eventToDelete, setEventToDelete] = useState<EventListResponse | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const navigate = useNavigate();
  const { t, i18n } = useTranslation("events");

  const getUsername = (): string => {
    try {
      const userJson = localStorage.getItem("loginResponse");
      if (!userJson) return "";
      const user = JSON.parse(userJson);
      return user.username || user.email?.split("@")[0] || "";
    } catch {
      return "";
    }
  };

  useEffect(() => {
    fetchCountries()
      .then((data) => {
        if (Array.isArray(data)) {
          setApiLocations(data);
        }
      })
      .catch(() => { });
  }, []);

  const fetchEventsList = async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const eventsData = await getAllEvents(getUsername());

      setEventsList(Array.isArray(eventsData) ? eventsData : []);
    } catch (error: any) {
      console.error("Error fetching events list:", error);
      setEventsList([]);
      setErrorMessage("");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEventsList();
  }, []);

  const handleEventSaved = () => {
    fetchEventsList();
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setLocationFilter("");
  };

  const handleEditClick = async (eventItem: EventListResponse) => {
    try {
      const username = getUsername();
      const detail = await getEventDetail(eventItem.id, username);
      setEditingEventData(detail);
      setIsCreateOpen(true);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
        t("failed_to_load_event_details", "Failed to load event details")
      );
    }
  };

  const canDeleteEventItem = (eventItem: EventListResponse): boolean =>
    Number(eventItem.matchupCount || 0) === 0;

  const handleOpenDeleteConfirm = (eventItem: EventListResponse) => {
    if (!canDeleteEventItem(eventItem)) {
      return;
    }

    setEventToDelete(eventItem);
  };

  const handleConfirmDelete = async () => {
    if (!eventToDelete) return;
    setIsDeleting(true);
    try {
      const username = getUsername();
      await deleteEvent(username, eventToDelete.id);
      toast.success(
        t("event_deleted_successfully", "Event deleted successfully")
      );
      setEventToDelete(null);
      fetchEventsList();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        t("failed_to_delete_event", "Failed to delete event");
      toast.error(msg);
    } finally {
      setIsDeleting(false);
    }
  };

  const locations = useMemo(() => {
    const locSet = new Set<string>(apiLocations);

    eventsList.forEach((event) => {
      const eventCountry = event.country || event.location;

      if (eventCountry && eventCountry.trim()) {
        locSet.add(eventCountry.trim());
      }
    });

    return Array.from(locSet);
  }, [eventsList, apiLocations]);

  const filteredEvents = useMemo(() => {
    return eventsList.filter((event) => {
      const search = searchQuery.toLowerCase().trim();

      const eventName = (event.name || "").toLowerCase();
      const eventVenue = (event.venue || "").toLowerCase();
      const eventCountry = (event.country || event.location || "").toLowerCase();
      const eventCity = (event.city || "").toLowerCase();
      const organizer = (
        event.organizer
          ? `${event.organizer.firstName || ""} ${event.organizer.lastName || ""}`
          : event.organizerName || ""
      ).toLowerCase();

      const matchesSearch =
        !search ||
        eventName.includes(search) ||
        eventVenue.includes(search) ||
        eventCountry.includes(search) ||
        eventCity.includes(search) ||
        organizer.includes(search);

      const matchesLocation =
        !locationFilter || (event.country || event.location) === locationFilter;

      return matchesSearch && matchesLocation;
    });
  }, [eventsList, searchQuery, locationFilter]);

  return (
    <div className="w-full min-h-[90vh] flex flex-col bg-[#f8fafc]">
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200/80 px-4 sm:px-6 py-4 flex-shrink-0 shadow-sm">
        <div className="w-full">
          <EventFilters
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            locationFilter={locationFilter}
            setLocationFilter={setLocationFilter}
            locations={locations}
            handleResetFilters={handleResetFilters}
            onCreateEvent={() => {
              setEditingEventData(null);
              setIsCreateOpen(true);
            }}
          />
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-6">
        <div className="w-full">
          {errorMessage && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-2xl flex items-center justify-between">
              <span className="text-sm text-blue-700 font-medium">
                {errorMessage}
              </span>

              <Button
                size="sm"
                color="primary"
                variant="flat"
                onPress={() => fetchEventsList()}
                className="rounded-xl font-medium"
              >
                {t("retry", "Retry")}
              </Button>
            </div>
          )}

          {isLoading ? (
            <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 animate-pulse">
              {Array.from({ length: 10 }).map((_, index) => (
                <div
                  key={index}
                  className="bg-white rounded-3xl border border-slate-200/90 shadow-[0px_1px_3px_rgba(0,0,0,0.04)] p-2 flex flex-col justify-between h-[380px]"
                >
                  <div className="w-full h-52 rounded-[20px] bg-slate-200/80" />
                  <div className="p-2 flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="h-4 w-16 bg-slate-200 rounded-lg" />
                        <div className="h-4 w-20 bg-slate-200 rounded-lg" />
                      </div>
                      <div className="h-5 w-4/5 bg-slate-200 rounded-lg mb-2" />
                      <div className="h-3 w-1/2 bg-slate-100 rounded" />
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                      <div className="h-4 w-16 bg-slate-200 rounded" />
                      <div className="h-6 w-16 bg-slate-200 rounded-xl" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-20 px-4 text-slate-500 bg-white border border-dashed border-slate-200 rounded-3xl max-w-lg mx-auto mt-6 shadow-sm">
              <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-3 text-blue-600">
                <CalendarDaysIcon className="w-7 h-7" />
              </div>

              <h3 className="text-base font-bold text-slate-800">
                {t("no_events_found", "No events found")}
              </h3>

              <p className="text-xs text-slate-500 mt-1 max-w-xs mx-auto">
                {eventsList.length === 0
                  ? t("get_started_events", "Get started by creating your first combat sports event!")
                  : t("try_changing_filters", "Try clearing or changing your search filters.")}
              </p>

              {eventsList.length > 0 && (
                <Button
                  className="mt-5 rounded-xl font-medium"
                  color="primary"
                  variant="flat"
                  size="sm"
                  onPress={handleResetFilters}
                >
                  {t("reset_filters", "Reset Filters")}
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="lg:hidden flex flex-col gap-3">
              {filteredEvents.map((event) => {
                const canDeleteEvent = canDeleteEventItem(event);
                const eventImage =
                  (event as any).posterUrl ||
                  (event as any).bannerUrl ||
                  (event as any).imageUrl ||
                  event.organizer?.profileImage;

                const category = (event as any).category || (event as any).tier || "Amateur";
                const weightCategory =
                  (event as any).weightClass || (event as any).discipline || event.venue || "Strawweight";
                const purseOrBouts =
                  (event as any).purse ||
                  (event as any).price ||
                  (event.matchupCount !== undefined && event.matchupCount !== null
                    ? `${event.matchupCount} ${event.matchupCount === 1 ? t("bout", "Bout") : t("bouts", "Bouts")}`
                    : "± 1000 €");

                return (
                  <div
                    key={`m-${event.id}`}
                    onClick={() => navigate(`/events/${event.id}`)}
                    className="bg-white rounded-2xl border border-slate-200 shadow-sm active:scale-[0.99] transition-transform cursor-pointer overflow-hidden flex flex-col"
                  >
                    <div className="w-full h-44 bg-slate-100 relative overflow-hidden flex items-center justify-center">
                      {eventImage ? (
                        <img
                          src={eventImage}
                          alt={event.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-100 via-slate-50 to-slate-200 select-none">
                          <div className="w-16 h-16 rounded-full border-4 border-blue-600/30 flex items-center justify-center mb-1">
                            <span className="text-base font-black text-slate-800 tracking-wider">ICO</span>
                          </div>
                          <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">MARTIAL ARTS</span>
                        </div>
                      )}

                      <div
                        className="absolute top-2.5 right-2.5 flex items-center gap-1 bg-white/90 backdrop-blur-sm p-0.5 rounded-full shadow-sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          isIconOnly
                          size="sm"
                          variant="light"
                          onPress={() => handleEditClick(event)}
                          className="h-7 w-7 min-w-7 rounded-full text-slate-600 hover:text-blue-600 hover:bg-blue-50"
                          aria-label={t("edit", "Edit")}
                        >
                          <PencilIcon className="w-3.5 h-3.5" />
                        </Button>

                        {canDeleteEvent && (
                          <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            onPress={() => handleOpenDeleteConfirm(event)}
                            className="h-7 w-7 min-w-7 rounded-full text-slate-600 hover:text-red-600 hover:bg-red-50"
                            aria-label={t("delete", "Delete")}
                          >
                            <TrashIcon className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="px-4 pt-3 pb-3.5 flex flex-col gap-2">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-bold text-slate-900 text-sm leading-snug line-clamp-2 flex-1" title={event.name}>
                          {event.name}
                        </h3>
                        <span className="text-xs font-bold text-slate-900 shrink-0 whitespace-nowrap">{purseOrBouts}</span>
                      </div>

                      <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <CalendarDaysIcon className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                          <span className="truncate">{formatDisplayDate(getEventStartDate(event), t("tbd", "TBD"), i18n.language)}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <svg className="w-3.5 h-3.5 text-slate-600" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                          </svg>
                          <span>{category}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <svg className="w-3.5 h-3.5 text-slate-600 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v18m-9-6l9 3 9-3M3 9l9-3 9 3" />
                          </svg>
                          <span className="truncate">{weightCategory}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0 min-w-0">
                          <MapPinIcon className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                          <span className="truncate max-w-[120px]" title={formatEventLocation(event)}>{formatEventLocation(event)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="hidden lg:grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {filteredEvents.map((event) => {
                const canDeleteEvent = canDeleteEventItem(event);
                const eventImage =
                  (event as any).posterUrl ||
                  (event as any).bannerUrl ||
                  (event as any).imageUrl ||
                  event.organizer?.profileImage;

                const category = (event as any).category || (event as any).tier || "Amateur";
                const weightCategory =
                  (event as any).weightClass || (event as any).discipline || event.venue || "Strawweight";
                const purseOrBouts =
                  (event as any).purse ||
                  (event as any).price ||
                  (event.matchupCount !== undefined && event.matchupCount !== null
                    ? `${event.matchupCount} ${event.matchupCount === 1 ? t("bout", "Bout") : t("bouts", "Bouts")}`
                    : "± 1000 €");

                return (
                  <div
                    key={event.id}
                    onClick={() => navigate(`/events/${event.id}`)}
                    className="bg-white rounded-3xl border border-slate-200/90 shadow-[0px_1px_3px_rgba(0,0,0,0.04)] hover:shadow-lg hover:border-slate-300 transition-all p-2 flex flex-col justify-between group overflow-hidden cursor-pointer h-[380px]"
                  >
                    <div className="w-full h-52 rounded-[20px] bg-slate-100 relative overflow-hidden flex items-center justify-center p-2">
                      {eventImage ? (
                        <img
                          src={eventImage}
                          alt={event.name}
                          className="w-full h-full object-cover rounded-[18px]"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-100 via-slate-50 to-slate-200 rounded-[18px] p-4 text-center select-none relative">
                          <div className="w-20 h-20 rounded-full border-4 border-blue-600/30 flex items-center justify-center mb-1">
                            <span className="text-xl font-black text-slate-800 tracking-wider">ICO</span>
                          </div>
                          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">MARTIAL ARTS</span>
                        </div>
                      )}

                      <div
                        className="absolute top-3 right-3 flex items-center gap-1.5 opacity-90 group-hover:opacity-100 transition-opacity bg-white/90 backdrop-blur-sm p-1 rounded-full shadow-sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Tooltip content={t("edit", "Edit")} closeDelay={0}>
                          <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            onPress={() => handleEditClick(event)}
                            className="h-7 w-7 min-w-7 rounded-full text-slate-600 hover:text-blue-600 hover:bg-blue-50"
                            aria-label={t("edit", "Edit")}
                          >
                            <PencilIcon className="w-3.5 h-3.5" />
                          </Button>
                        </Tooltip>

                        {canDeleteEvent && (
                          <Tooltip content={t("delete", "Delete")} color="danger" closeDelay={0}>
                            <Button
                              isIconOnly
                              size="sm"
                              variant="light"
                              onPress={() => handleOpenDeleteConfirm(event)}
                              className="h-7 w-7 min-w-7 rounded-full text-slate-600 hover:text-red-600 hover:bg-red-50"
                              aria-label={t("delete", "Delete")}
                            >
                              <TrashIcon className="w-3.5 h-3.5" />
                            </Button>
                          </Tooltip>
                        )}
                      </div>
                    </div>

                    <div className="px-3.5 pt-2.5 pb-3 flex flex-col justify-between flex-1 gap-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <h3
                          className="font-bold text-slate-900 text-sm leading-snug line-clamp-2 flex-1"
                          title={event.name}
                        >
                          {event.name}
                        </h3>
                        <span className="text-xs font-bold text-slate-900 shrink-0 whitespace-nowrap">
                          {purseOrBouts}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-2 text-xs text-slate-600 font-normal">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <CalendarDaysIcon className="w-4 h-4 text-slate-800 shrink-0" />
                          <span className="truncate">
                            {formatDisplayDate(getEventStartDate(event), t("tbd", "TBD"), i18n.language)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <svg className="w-3.5 h-3.5 text-slate-800" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                          </svg>
                          <span>{category}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-2 text-xs text-slate-600 font-normal">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <svg className="w-3.5 h-3.5 text-slate-800 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v18m-9-6l9 3 9-3M3 9l9-3 9 3" />
                          </svg>
                          <span className="truncate">{weightCategory}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0 min-w-0">
                          <MapPinIcon className="w-3.5 h-3.5 text-slate-800 shrink-0" />
                          <span className="truncate max-w-[100px]" title={formatEventLocation(event)}>
                            {formatEventLocation(event)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            </>
          )}
        </div>
      </div>

      <CreateEditEvent
        isOpen={isCreateOpen}
        onClose={() => {
          setIsCreateOpen(false);
          setEditingEventData(null);
        }}
        onSuccess={handleEventSaved}
        editEventData={editingEventData}
      />

      <Modal
        isOpen={!!eventToDelete}
        onOpenChange={(open) => {
          if (!open) {
            setEventToDelete(null);
          }
        }}
        size="sm"
        backdrop="opaque"
        classNames={{
          backdrop: "bg-black/40",
          base: "bg-white rounded-3xl shadow-xl p-2",
          header: "px-6 pt-5 pb-2 border-b border-slate-100 font-bold text-slate-900",
          body: "px-6 py-4 text-slate-600 text-sm",
          footer: "px-6 pb-5 pt-3 border-t border-slate-100 flex justify-end gap-2",
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="text-base font-bold text-slate-900">
                {t("confirm_delete_title", "Confirm Delete")}
              </ModalHeader>
              <ModalBody>
                <p>
                  {t(
                    "confirm_delete_message",
                    "Are you sure you want to delete this event? This action cannot be undone."
                  )}
                </p>
                {eventToDelete && (
                  <p className="mt-2 text-xs font-semibold text-slate-800 bg-slate-50 p-2.5 rounded-xl border border-slate-200 truncate">
                    {eventToDelete.name}
                  </p>
                )}
              </ModalBody>
              <ModalFooter>
                <Button
                  variant="light"
                  size="sm"
                  onPress={onClose}
                  className="text-slate-600 font-medium rounded-xl"
                >
                  {t("cancel", "Cancel")}
                </Button>
                <Button
                  color="danger"
                  size="sm"
                  isLoading={isDeleting}
                  onPress={handleConfirmDelete}
                  className="font-medium bg-red-600 text-white rounded-xl shadow-none"
                >
                  {t("delete", "Delete")}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default Events;
